import { DateTime } from 'luxon';

import {
  ItemPreparationStatuses,
  OrderInternalStatuses,
  OrderPreparationStatuses,
  OrderStatuses,
} from './enums';
import type { Order, OrderItem, StartPreparationStatus } from './types';

export const getStartPreparationStatusWeight = (
  startPreparationStatus?: StartPreparationStatus,
): number => {
  switch (startPreparationStatus) {
    case 'late':
      return 0;
    case 'risk_late':
      return 1;
    case 'now':
      return 2;
    case 'early':
      return 3;
    default:
      return 4;
  }
};

type OrderAndItems = {
  order: Order;
  items: OrderItem[];
};

export const getOrderInternalStatusWeight = ({
  order,
  items,
}: OrderAndItems): number => {
  const orderInternalStatus = getOrderInternalStatus(order, items);

  switch (orderInternalStatus) {
    case OrderInternalStatuses.BEING_PREPARED:
      return 0;
    case OrderInternalStatuses.NOT_BEING_PREPARED:
      return 1;
    case OrderInternalStatuses.PREPARED:
      return 2;
    case OrderInternalStatuses.SCHEDULED:
      return 3;
    case OrderInternalStatuses.CANCELLED:
    default:
      return 4;
  }
};

export const checkIfScheduledOrder = (order: Order) => {
  if (!order) return false;

  const scheduledAppearanceTime = DateTime.fromISO(
    order.scheduled_appearance_time,
  );

  return (
    scheduledAppearanceTime.isValid && scheduledAppearanceTime > DateTime.now()
  );
};

export const getOrderInternalStatus = (
  order: Order,
  items: OrderItem[],
): OrderInternalStatuses => {
  if (
    order.order_status === OrderStatuses.CANCELLED ||
    order.status === OrderPreparationStatuses.CANCELLED
  ) {
    // NOTE: got changed to PREPARE in redux
    return OrderInternalStatuses.CANCELLED;
  }

  if (
    order.status === OrderPreparationStatuses.COMPLETED ||
    order.status === OrderPreparationStatuses.PREPARED
  ) {
    return OrderInternalStatuses.PREPARED;
  }

  if (order.status === OrderPreparationStatuses.BEING_PREPARED) {
    const cookableItems = items.filter((item) => item.cookable);

    const hasSomeCookableItemNotBeingPrepared = cookableItems.some(
      (item) => item.status === ItemPreparationStatuses.NOT_BEING_PREPARED,
    );

    if (hasSomeCookableItemNotBeingPrepared) {
      return OrderInternalStatuses.NOT_BEING_PREPARED;
    }

    const hasSomeCookableItemBeingPrepared = cookableItems.some(
      (item) => item.status === ItemPreparationStatuses.BEING_PREPARED,
    );

    if (hasSomeCookableItemBeingPrepared) {
      return OrderInternalStatuses.BEING_PREPARED;
    }
    return OrderInternalStatuses.PREPARED;
  }

  if (checkIfScheduledOrder(order)) {
    return OrderInternalStatuses.SCHEDULED;
  }

  return OrderInternalStatuses.NOT_BEING_PREPARED;
};
